import { useMemo, useState, type CSSProperties } from 'react'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Icon } from '@iconify/react'
import { Checkbox, Popover, Tag } from 'antd'
import classNames from 'classnames'

import {
  shiftRoomOrder,
  type ScheduleRoomOption,
  type ScheduleRoomsPreference,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleRoomsOrder'

import styles from './roomsOrder.module.scss'

const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
})

type RoomsOrderProps = {
  rooms: ScheduleRoomOption[]
  order: string[]
  hidden: string[]
  onChange: (next: ScheduleRoomsPreference) => void
  disabled?: boolean
}

type RoomOrderRowProps = {
  room: ScheduleRoomOption
  index: number
  count: number
  visible: boolean
  onToggle: (id: string) => void
  onOnly: (id: string) => void
  onShift: (id: string, direction: -1 | 1) => void
}

const RoomOrderRow = ({
  room,
  index,
  count,
  visible,
  onToggle,
  onOnly,
  onShift,
}: RoomOrderRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: room.id,
  })
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(styles.row, isDragging && styles.dragging)}>
      <span
        className={styles.handle}
        aria-label={`Reorder ${room.text}`}
        {...attributes}
        {...listeners}>
        <Icon icon='ic:round-drag-indicator' width={16} height={16} />
      </span>
      <Checkbox
        className={styles.check}
        checked={visible}
        disabled={room.locked}
        onChange={() => onToggle(room.id)}>
        <span className={styles.roomName}>{room.text}</span>
      </Checkbox>
      <button
        type='button'
        className={styles.textButton}
        disabled={room.locked}
        onClick={() => onOnly(room.id)}>
        Only
      </button>
      <button
        type='button'
        className={styles.textButton}
        aria-label={`Move ${room.text} up`}
        disabled={index === 0}
        onClick={() => onShift(room.id, -1)}>
        <Icon icon='ion:chevron-up' width={18} height={18} />
      </button>
      <button
        type='button'
        className={styles.textButton}
        aria-label={`Move ${room.text} down`}
        disabled={index === count - 1}
        onClick={() => onShift(room.id, 1)}>
        <Icon icon='ion:chevron-down' width={18} height={18} />
      </button>
    </div>
  )
}

export const RoomsOrder = ({
  rooms,
  order,
  hidden,
  onChange,
  disabled = false,
}: RoomsOrderProps) => {
  const [open, setOpen] = useState(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )
  const byId = useMemo(() => new Map(rooms.map((room) => [room.id, room])), [rooms])
  const rows = order.flatMap((id) => {
    const room = byId.get(id)
    return room ? [room] : []
  })
  const hiddenSet = new Set(hidden)
  const visibleRows = rows.filter((room) => room.locked || !hiddenSet.has(room.id))
  const summary =
    !rows.length || visibleRows.length === rows.length
      ? rows.length
        ? 'All'
        : 'None'
      : visibleRows.length
        ? visibleRows.map((room) => room.text).join(', ')
        : 'None'

  const emit = (next: Partial<ScheduleRoomsPreference>) => {
    onChange({ order, hidden, ...next })
  }

  const toggle = (id: string) => {
    const room = byId.get(id)
    if (!room || room.locked) return
    emit({
      hidden: hiddenSet.has(id) ? hidden.filter((item) => item !== id) : [...hidden, id],
    })
  }

  const only = (id: string) => {
    const room = byId.get(id)
    if (!room || room.locked) return
    emit({
      hidden: rooms.filter((item) => item.id !== id && !item.locked).map((item) => item.id),
    })
  }

  const showAll = () => emit({ hidden: [] })
  const hideAll = () => emit({ hidden: rooms.filter((room) => !room.locked).map((room) => room.id) })
  const defaultOrder = rooms.map((room) => room.id)
  const isReset =
    order.length === defaultOrder.length &&
    order.every((id, index) => id === defaultOrder[index]) &&
    !hidden.some((id) => byId.has(id))
  const reset = () => onChange({ order: defaultOrder, hidden: [] })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = order.indexOf(String(active.id))
    const newIndex = order.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    emit({ order: arrayMove(order, oldIndex, newIndex) })
  }

  const panel = (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Rooms</span>
        <div className={styles.headerActions}>
          <button type='button' className={styles.linkButton} onClick={showAll}>
            Show all
          </button>
          <button type='button' className={styles.linkButton} onClick={hideAll}>
            Hide all
          </button>
        </div>
      </div>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map((room) => room.id)} strategy={verticalListSortingStrategy}>
          <div className={styles.list}>
            {rows.map((room, index) => (
              <RoomOrderRow
                key={room.id}
                room={room}
                index={index}
                count={rows.length}
                visible={room.locked || !hiddenSet.has(room.id)}
                onToggle={toggle}
                onOnly={only}
                onShift={(id, direction) => emit({ order: shiftRoomOrder(order, id, direction) })}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className={styles.footer}>
        <span className={styles.hint}>Drag to reorder columns</span>
        <button type='button' className={styles.resetButton} onClick={reset} disabled={isReset}>
          Reset
        </button>
      </div>
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger='click'
      placement='bottomRight'
      arrow={false}
      content={panel}
      rootClassName={styles.popover}
      destroyOnHidden>
      <button type='button' className={styles.trigger} disabled={disabled} aria-haspopup='dialog'>
        <span className={styles.triggerLabel}>Rooms</span>
        <span className={styles.summary}>{summary}</span>
        <Tag bordered={false} className={styles.count}>
          {visibleRows.length}/{rows.length}
        </Tag>
        <Icon className={styles.chevron} icon='ion:chevron-down' width={12} height={12} />
      </button>
    </Popover>
  )
}
